import { Link, isRouteErrorResponse, useRouteError } from 'react-router';

/**
 * The router's last net.
 *
 * Without it React Router renders its own developer notice in production. This
 * is the internal console, so the audience is us — but "Hey developer 👋" still
 * tells nobody which of the two things happened.
 *
 * Two exports on purpose: `NotFoundPage` is a route ELEMENT (the catch-all),
 * where `useRouteError()` returns nothing; `RouteErrorPage` is an errorElement,
 * where it returns the thrown value. Using one component for both would report
 * every mistyped URL as a crash.
 */
function ErrorShell({
  title, body, showReload,
}: {
  title: string;
  body: string;
  showReload?: boolean;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50 px-4">
      <div className="w-full max-w-sm rounded-lg border border-surface-200 bg-surface-0 p-8 text-center shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-surface-900">{title}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-surface-500">{body}</p>
        <div className="mt-6 flex justify-center gap-2">
          {showReload && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-surface-300 px-3.5 py-2 text-sm font-medium text-surface-800 transition-colors hover:bg-surface-100"
            >
              Tải lại
            </button>
          )}
          <Link
            to="/orgs"
            className="rounded-md bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            Về danh sách tổ chức
          </Link>
        </div>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <ErrorShell
      title="Không tìm thấy trang"
      body="Đường dẫn này không tồn tại trong trang quản trị."
    />
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  return (
    <ErrorShell
      title="Có lỗi xảy ra"
      body="Trang quản trị gặp trục trặc khi mở mục này. Thử tải lại, và xem log nếu vẫn vậy."
      showReload
    />
  );
}
